import PolicyLayout, { PolicyCallout, PolicyList, PolicySection } from "@/pages/policies/components/PolicyLayout";

const GUEST_PLANS = [
  {
    name: "Premier",
    price: "Highest plan fee",
    max: "KSh 0",
    detail: "Zero out-of-pocket for covered physical damage. Available to drivers aged 21 and over, excludes certain high-value cars.",
    highlight: true,
  },
  {
    name: "Standard",
    price: "Mid plan fee",
    max: "KSh 25,000",
    detail: "Caps your out-of-pocket responsibility for covered physical damage. Available to drivers aged 18 and over.",
  },
  {
    name: "Minimum",
    price: "Lowest plan fee",
    max: "KSh 100,000",
    detail: "The most affordable option, with the highest out-of-pocket responsibility for covered physical damage.",
  },
  {
    name: "Decline",
    price: "No plan fee",
    max: "Full value",
    detail: "You rely on your own insurance and carry full responsibility for any damage. Third-party cover is only at statutory minimums.",
  },
];

const HOST_PLANS = [
  { name: "More peace of mind", share: "70%", max: "KSh 10,000" },
  { name: "Balanced", share: "80%", max: "KSh 60,000" },
  { name: "More earnings", share: "90%", max: "KSh 110,000" },
];

export default function InsurancePage() {
  return (
    <PolicyLayout
      eyebrow="Protection"
      title="Protection Plans & Insurance"
      updated="1 October 2026"
      intro="Protection plans let riders limit what they could owe for accidental damage, and let hosts choose a balance between earnings and risk. This page explains the tiers, what is covered, what is not, and how a claim is handled. Figures are indicative and confirmed at checkout."
    >
      <PolicySection id="how" title="1. How protection works">
        <p>
          At checkout riders pick a protection plan, and hosts pick an earnings plan when they list a car. The plan
          you choose sets your financial responsibility (the &quot;maximum out-of-pocket&quot;) for covered physical
          damage such as collision, theft or vandalism, and the level of third-party liability cover that comes with
          the trip.
        </p>
        <PolicyCallout icon="ri-information-line">
          Third-party liability is arranged through a licensed insurance partner and sits alongside any personal motor
          policy. Cover amounts, deductible and exclusions are governed by the insurer&apos;s terms.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="guest" title="2. Rider protection plans">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {GUEST_PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-xl border p-4 ${
                plan.highlight ? "border-primary-500/50 bg-primary-500/10" : "border-background-200 bg-background-50"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-heading text-base font-semibold text-foreground-950">{plan.name}</h3>
                <span className="rounded-full bg-background-200/70 px-2.5 py-1 text-xs font-medium text-foreground-800">
                  {plan.price}
                </span>
              </div>
              <p className="mt-3 text-xs uppercase tracking-wider text-foreground-500">Max out-of-pocket</p>
              <p className="font-heading text-2xl font-semibold text-primary-500">{plan.max}</p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{plan.detail}</p>
            </div>
          ))}
        </div>
      </PolicySection>

      <PolicySection id="host" title="3. Host earnings plans">
        <p>
          Hosts choose how much of each trip price they keep. Higher earnings shares come with more responsibility for
          eligible damage to their own car.
        </p>
        <div className="overflow-hidden rounded-xl border border-background-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-background-100 text-xs uppercase tracking-wider text-foreground-500">
              <tr>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">Host share</th>
                <th className="px-4 py-3 font-medium">Damage responsibility</th>
              </tr>
            </thead>
            <tbody>
              {HOST_PLANS.map((plan) => (
                <tr key={plan.name} className="border-t border-background-200">
                  <td className="px-4 py-3 font-medium text-foreground-900">{plan.name}</td>
                  <td className="px-4 py-3 text-foreground-600">{plan.share} of trip price</td>
                  <td className="px-4 py-3 text-foreground-600">Up to {plan.max} per claim</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-foreground-500">
          Hosts also receive third-party liability cover on every trip, arranged through our insurance partner.
        </p>
      </PolicySection>

      <PolicySection id="covered" title="4. What is covered">
        <PolicyList
          items={[
            "Accidental physical damage to the vehicle caused during an approved trip.",
            "Theft of the vehicle during an approved trip, subject to the plan and police reporting.",
            "Vandalism occurring during an approved trip.",
            "Third-party liability at the level provided by the plan and the insurer.",
          ]}
        />
      </PolicySection>

      <PolicySection id="excluded" title="5. What is not covered">
        <PolicyList
          items={[
            "Interior wear and tear, and mechanical breakdown not caused by an accident.",
            "Damage caused while the car is used in a prohibited way (see Guest Standards).",
            "Damage or loss while no approved trip is active.",
            "Driving by an unapproved driver, or by someone without a valid licence.",
            "Personal belongings left in the car, and fines, tolls or impound charges.",
          ]}
        />
        <PolicyCallout icon="ri-error-warning-line">
          Breaching the Terms of Service or Guest Standards — for example smoking, off-road use or letting an
          unapproved person drive — can void your protection entirely.
        </PolicyCallout>
      </PolicySection>

      <PolicySection id="documentation" title="6. Documentation you should keep">
        <p>
          Protection depends on evidence. Before pickup and after the ride, record a camera walkaround of the car
          through the booking hub. These clips include timestamps and are stored privately on the booking.
        </p>
        <PolicyList
          items={[
            "Film all sides of the car plus the interior and wheels at handover.",
            "Note the fuel level, mileage and any existing scratches in the condition notes.",
            "Report any incident, accident or theft to the host and to Gari immediately.",
            "Keep any police report or third-party details if an incident occurs.",
          ]}
        />
      </PolicySection>

      <PolicySection id="claims" title="7. How a claim is handled">
        <PolicyList
          items={[
            "Flag the damage on the booking with a note, or contact support within the claim window after the trip.",
            "Our team reviews the before/after walkaround clips, condition notes and any reports.",
            "A verdict is logged on the booking and can be disputed or resolved by either party.",
            "Approved claims are handled by our insurance partner, who confirms the final amount and deductible.",
          ]}
        />
      </PolicySection>
    </PolicyLayout>
  );
}